import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Calendar, 
  BarChart3, 
  Download,
  Filter,
  Search,
  AlertCircle,
  CheckCircle,
  Timer
} from 'lucide-react';
import { ClientDashboardLayout } from './ClientDashboardLayout';
import { UniversalTimeTracker } from '../../components/TimeTracker';
import { useTimeTracker } from '../../hooks/useTimeTracker';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface TimeEntry {
  id: string;
  taskId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  endTime?: Date;
  duration: number; // in minutes
  billable: boolean;
  status: string;
  date: string;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  dueDate?: string;
}

interface TimeStats {
  todayHours: number;
  weekHours: number;
  monthHours: number;
  totalHours: number;
}

export const ClientTimeTracking: React.FC = () => {
  const { user } = useAuthStore();
  const timeTracker = useTimeTracker(user?.userId);
  
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TimeStats>({
    todayHours: 0,
    weekHours: 0,
    monthHours: 0,
    totalHours: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTimeEntries();
    fetchTasks();
    fetchTimeStats();
  }, [selectedDate]);

  const fetchTimeEntries = async () => {
    try {
      const response = await api.get(`/time-entries/my-entries?date=${selectedDate}`);
      if (response.data.success) {
        setTimeEntries(response.data.data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching time entries:', error);
      toast.error('Failed to load time entries');
    }
  };

  const fetchTasks = async () => {
    try {
      // Fetch tasks assigned to the client
      const response = await api.get('/tasks/my-tasks');
      if (response.data.success) {
        setTasks(response.data.data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // Don't show error toast as tasks might not be available for clients
    }
  };

  const fetchTimeStats = async () => {
    try {
      const response = await api.get('/time-entries/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching time stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeEntryCreated = () => {
    fetchTimeEntries();
    fetchTimeStats();
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const filteredEntries = timeEntries.filter(entry =>
    entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.taskTitle && entry.taskTitle.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <ClientDashboardLayout currentPage="time-tracking">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </ClientDashboardLayout>
    );
  }

  return (
    <ClientDashboardLayout currentPage="time-tracking">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Time Tracking</h1>
            <p className="text-gray-600 mt-1">Track your time and manage your work hours</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                // Export functionality could be added here
                toast.info('Export functionality coming soon');
              }}
              className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Time Tracker */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <UniversalTimeTracker
            userRole="Client"
            userId={user?.userId || ''}
            availableTasks={tasks}
            onTimeEntryCreated={handleTimeEntryCreated}
            onTimerStarted={() => fetchTimeStats()}
            onTimerStopped={handleTimeEntryCreated}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Today</p>
                <p className="text-2xl font-bold text-gray-900">{stats.todayHours.toFixed(1)}h</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900">{stats.weekHours.toFixed(1)}h</p>
              </div>
              <Calendar className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">{stats.monthHours.toFixed(1)}h</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalHours.toFixed(1)}h</p>
              </div>
              <Timer className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search time entries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Time Entries List */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900">
              Time Entries for {new Date(selectedDate).toLocaleDateString()}
            </h3>
            
            {filteredEntries.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No time entries found for this date</p>
                <p className="text-sm text-gray-400 mt-1">Start tracking time to see entries here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredEntries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center">
                          {entry.billable ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="ml-2 text-sm text-gray-600">
                            {entry.billable ? 'Billable' : 'Non-billable'}
                          </span>
                        </div>
                        
                        <span className="font-medium text-gray-900">
                          {formatDuration(entry.duration)}
                        </span>
                        
                        <span className="text-sm text-gray-600">
                          {new Date(entry.startTime).toLocaleTimeString()} - 
                          {entry.endTime ? new Date(entry.endTime).toLocaleTimeString() : 'Running'}
                        </span>
                      </div>
                      
                      <div className="mt-1">
                        {entry.taskTitle && (
                          <span className="text-sm font-medium text-primary-600 mr-2">
                            {entry.taskTitle}
                          </span>
                        )}
                        <span className="text-sm text-gray-700">{entry.description}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ClientDashboardLayout>
  );
};
